import { scrollElementIntoViewWithOffset } from "@/main/common/ScrollUtils";
import type { PassageBlockMap } from "@/main/react/discussion/passage/mapBlocksToDom";

/**
 * Where a thought card's passage chip finds the block it points at.
 *
 * `DiscussionContentDisplayer` owns the block map (`mapBlocksToDom`, D4) and the cards live in a
 * different subtree several props away, so the map is published here by discussion id and read back by
 * the chip at tap time. A plain module registry, deliberately NOT a subscribable store: nothing renders
 * off it, so it has no listeners to notify and rule 31 does not apply. A second copy of the map anywhere
 * else would be the defect rule 31 names.
 *
 * Highlight class: `passage-block-jump-target`, defined in `07-seavien-content-wrapper.css` with a
 * reduced-motion branch that skips the pulse (design §7).
 */
const PASSAGE_BLOCK_JUMP_HIGHLIGHT_CLASS = "passage-block-jump-target";
const PASSAGE_BLOCK_JUMP_HIGHLIGHT_MS = 1600;

const registry = new Map<string, PassageBlockMap>();

export function registerPassageBlocks(discussionPublicId: string | null | undefined, map: PassageBlockMap | null): void {
    const key = (discussionPublicId ?? "").trim();
    if (!key) {
        return;
    }
    if (map && map.size > 0) {
        registry.set(key, map);
    } else {
        registry.delete(key);
    }
}

export function getPassageBlockElement(discussionPublicId: string | null | undefined, blockId: string | null | undefined): HTMLElement | null {
    const key = (discussionPublicId ?? "").trim();
    const id = (blockId ?? "").trim();
    if (!key || !id) {
        return null;
    }
    const element = registry.get(key)?.get(id) ?? null;
    return element && element.isConnected ? element : null;
}

/**
 * Scrolls the block into view and highlights it briefly. Returns false when the block is unknown on
 * this page (the map was refused, the block is gone, or the page is another post), in which case the
 * caller does nothing: a chip that cannot jump must not jump somewhere else.
 */
export function scrollToPassageBlock(discussionPublicId: string | null | undefined, blockId: string | null | undefined): boolean {
    const element = getPassageBlockElement(discussionPublicId, blockId);
    if (!element) {
        return false;
    }
    void scrollElementIntoViewWithOffset(element, { align: "middle" }).catch(() => {
        // Best effort: the highlight below still says where the passage is.
    });
    element.classList.add(PASSAGE_BLOCK_JUMP_HIGHLIGHT_CLASS);
    window.setTimeout(() => element.classList.remove(PASSAGE_BLOCK_JUMP_HIGHLIGHT_CLASS), PASSAGE_BLOCK_JUMP_HIGHLIGHT_MS);
    return true;
}
